//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Edit, TrashCan } from '@carbon/icons-react';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useTranslation } from 'react-i18next';
import LoopDataService from '../../utils/LoopDataService';
import { useUserAuth } from '../../UserAuth';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';
import { useDashboardIframePolling } from '../Loops/useDashboardIframePolling';

type DashboardHit = {
  _id: string;
  _source: {
    dashboard: {
      teamspacename: string;
      description: string;
      title: string;
      Url: string;
      dateCreated: string;
      status: 'Online' | 'Offline';
      type:
        | 'overview'
        | 'detailed'
        | 'delivery'
        | 'qualityAndSecurity'
        | 'productivity';
    };
  };
};

const removeFullScreenButton = (iframeDoc: Document) => {
  try {
    const btn = iframeDoc.querySelector(
      '[data-test-subj="dashboardFullScreenMode"]',
    );
    if (btn) {
      btn.remove();
    }
  } catch (err) {
    console.error('ERROR removing fullscreen button:', err);
  }
};

const OpenSearchDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [dashboards, setDashboards] = useState<DashboardHit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedDashboardUrl, setSelectedDashboardUrl] = useState('');
  const { user, isAuthenticated } = useUserAuth();
  const [hideDashboardContainer, setHideDashboardContainer] =
    useState<boolean>(false);
  const [allLoop, setAllLoop] = useState<any[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { teamspaceId, teamspaceName } = useTeamspaceContext();
  const { activeLoopId, activeLoopName, isLoopContextActive } =
    useActiveLoopContext();
  const [activeDashboardLabel, setActiveDashboardLabel] = useState<{
    loopName: string;
    label: string;
  } | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);

  useDashboardIframePolling(
    iframeRef,
    selectedDashboardUrl,
    setDashboardLoaded,
    dashboardLoaded,
    hideDashboardContainer && !!selectedDashboardUrl,
  );

  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  const getDashboardLabel = (
    type: string,
    t: (key: string) => string,
  ): string => {
    switch (type) {
      case 'overview':
        return t('overview-dashboard');
      case 'detailed':
        return t('detail-dashboard');
      case 'delivery':
        return t('delivery-dashboard');
      case 'qualityAndSecurity':
        return t('quality-and-security-dashboard');
      case 'productivity':
        return t('productivity-dashboard');
      default:
        return t('dashboard');
    }
  };

  const fetchDashboardUrls = (
    dashboardUrl: string,
    type:
      | 'overview'
      | 'detailed'
      | 'delivery'
      | 'qualityAndSecurity'
      | 'productivity',
    loopName: string,
  ) => {
    setDashboardLoaded(false);
    setHideDashboardContainer(true);
    setActiveDashboardLabel({ loopName, label: getDashboardLabel(type, t) });
    setError(null);

    const isDevelopment = process.env.NODE_ENV === 'development';
    if (!isDevelopment && !teamspaceId) {
      setError('Teamspace ID is not available');
      return;
    }

    setSelectedDashboardUrl(dashboardUrl);
  };

  const fetchEditableDashboardUrl = (
    dashboardUrl: string,
    type:
      | 'overview'
      | 'detailed'
      | 'delivery'
      | 'qualityAndSecurity'
      | 'productivity',
    loopName: string,
  ) => {
    setDashboardLoaded(false);
    setError(null);

    const isDevelopment = process.env.NODE_ENV === 'development';
    if (!isDevelopment && !teamspaceId) {
      setError('Teamspace ID is not available');
      return;
    }

    setSelectedDashboardUrl(dashboardUrl);
    setActiveDashboardLabel({ loopName, label: getDashboardLabel(type, t) });
    setHideDashboardContainer(true);
  };

  const fetchAllUserLoops = useCallback(
    async (tsId: string) => {
      try {
        if (!tsId) {
          setError('Teamspace ID is not available');
          return;
        }

        const response = await loopDataService.getLoopUser(tsId);

        if (response && response._embedded && response._embedded.loops) {
          const loops = response._embedded.loops;

          const loopIds = loops.map((loop) => loop.id);

          // When a loop is active, only fetch dashboards for that loop, but
          // only if it's one of the user's loops in this teamspace — the
          // active loop ID can come from sessionStorage or a ?loopId= URL
          // param, neither of which is validated against membership.
          if (
            isLoopContextActive &&
            activeLoopId &&
            loopIds.includes(activeLoopId)
          ) {
            setAllLoop([activeLoopId]);
            return;
          }

          setAllLoop(loopIds);
        }
      } catch (error) {
        console.error('Error fetching loop users:', error);
        setError('Failed to load loop users');
      }
    },
    [loopDataService, isLoopContextActive, activeLoopId],
  );

  const transformResponseToDashboards = (
    response: Record<string, any>,
  ): DashboardHit[] => {
    const items: DashboardHit[] = [];
    // Overview Dashboard
    if (response.Overview) {
      items.push({
        _id: response.overviewId,
        _source: {
          dashboard: {
            teamspacename: response.loopName,
            description: response.overviewDescription,
            title: response.overviewTitle,
            Url: response.Overview,
            dateCreated: response.overviewDateCreated ?? '',
            status: response.overviewStatus ?? 'Online',
            type: 'overview',
          },
        },
      });
    }
    // Detailed Dashboard
    if (response.Detailed) {
      items.push({
        _id: response.detailedId,
        _source: {
          dashboard: {
            teamspacename: response.loopName,
            description: response.detailedDescription,
            title: response.detailedTitle,
            Url: response.Detailed,
            dateCreated: response.detailedDateCreated ?? '',
            status: response.detailedStatus ?? 'Online',
            type: 'detailed',
          },
        },
      });
    }
    // Delivery Dashboard
    if (response.Delivery) {
      items.push({
        _id: response.deliveryId,
        _source: {
          dashboard: {
            teamspacename: response.loopName,
            description: response.deliveryDescription,
            title: response.deliveryTitle,
            Url: response.Delivery,
            dateCreated: response.deliveryDateCreated ?? '',
            status: response.deliveryStatus ?? 'Online',
            type: 'delivery',
          },
        },
      });
    }
    // Quality and Security Dashboard
    if (response.QualityAndSecurity) {
      items.push({
        _id: response.qualityAndSecurityId,
        _source: {
          dashboard: {
            teamspacename: response.loopName,
            description: response.qualityAndSecurityDescription,
            title: response.qualityAndSecurityTitle,
            Url: response.QualityAndSecurity,
            dateCreated: response.qualityAndSecurityDateCreated ?? '',
            status: response.qualityAndSecurityStatus ?? 'Online',
            type: 'qualityAndSecurity',
          },
        },
      });
    }
    // Productivity Dashboard
    if (response.Productivity) {
      items.push({
        _id: response.productivityId,
        _source: {
          dashboard: {
            teamspacename: response.loopName,
            description: response.productivityDescription,
            title: response.productivityTitle,
            Url: response.Productivity,
            dateCreated: response.productivityDateCreated ?? '',
            status: response.productivityStatus ?? 'Online',
            type: 'productivity',
          },
        },
      });
    }
    return items;
  };

  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) {
      // In development mode, use dynamic import so mock data is tree-shaken in production
      import('./mockDashboard.json').then((module) => {
        const items = transformResponseToDashboards(module.default);
        setDashboards(items);
      });
      return;
    }
    if (!teamspaceId) return;
    fetchAllUserLoops(teamspaceId);
  }, [teamspaceId, fetchAllUserLoops]);

  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) return;
    if (!allLoop.length) return;
    const fetchData = async () => {
      try {
        if (!teamspaceId) {
          setError('Teamspace ID is not available');
          return;
        }
        const responses = await Promise.all(
          allLoop.map((loopid) =>
            loopDataService.getDashboardUrl(teamspaceId, loopid),
          ),
        );

        // Transform responses for frontend display
        const items: DashboardHit[] = [];
        responses.forEach((response) => {
          items.push(
            ...transformResponseToDashboards(response as Record<string, any>),
          );
        });

        setDashboards(items);
      } catch (err) {
        setError('Failed to load dashboards');
      }
    };

    fetchData();
  }, [allLoop, loopDataService, teamspaceId]);

  const handleBreadcrumb = () => {
    setHideDashboardContainer(false);
    setDashboardLoaded(false);
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  };

  return (
    <div
      id="id-dashboard-container"
      data-testid="dashboard-container"
      className="flex min-h-full flex-col bg-carbon-surfaceSecondary dark:bg-carbon-dark"
    >
      {/* Internal breadcrumb trail (only visible when viewing a dashboard iframe) */}
      {hideDashboardContainer && (
        <div
          id="id-dashboard-breadcrumb"
          data-testid="dashboard-breadcrumb"
          className="flex items-center gap-1 bg-carbon-surfaceSecondary px-4 py-2 dark:bg-carbon-dark"
        >
          <button
            className="text-sm text-brand-nav hover:underline dark:text-brand-linkDark"
            onClick={handleBreadcrumb}
            id="id-dashboard-breadcrumb-link"
            data-testid="id-dashboard-breadcrumb-link"
          >
            {isLoopContextActive && activeLoopName
              ? `${activeLoopName} ${t('dashboards')}`
              : teamspaceName || t('dashboards')}
          </button>
          <span
            id="id-dashboard-breadcrumb-separator"
            data-testid="dashboard-breadcrumb-separator"
            className="text-sm text-gray-400 dark:text-gray-500"
          >
            /
          </span>
          <span
            id="id-dashboard-breadcrumb-label"
            data-testid="dashboard-breadcrumb-label"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {activeDashboardLabel
              ? `${activeDashboardLabel.loopName} / ${activeDashboardLabel.label}`
              : ''}
          </span>
        </div>
      )}

      {/* Content area */}
      <div
        id="id-dashboard-content"
        data-testid="dashboard-content"
        className="flex-1 overflow-auto px-3 pt-3"
      >
        {error && (
          <p
            className="mb-3 text-sm text-red-500 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}

        {hideDashboardContainer ? (
          <div
            id="id-dashboard-iframe-wrapper"
            data-testid="dashboard-iframe-wrapper"
            className="relative"
          >
            {selectedDashboardUrl && (
              <>
                {!dashboardLoaded && (
                  <div
                    id="id-dashboard-skeleton"
                    data-testid="dashboard-skeleton"
                    className="absolute inset-x-0 top-0 mt-5 space-y-4"
                  >
                    <Skeleton className="h-8 w-3/4" />
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-[450px] w-full rounded-lg" />
                    <div
                      id="id-dashboard-skeleton-row"
                      data-testid="dashboard-skeleton-row"
                      className="flex gap-4"
                    >
                      <Skeleton className="h-6 w-1/4" />
                      <Skeleton className="h-6 w-1/4" />
                      <Skeleton className="h-6 w-1/4" />
                    </div>
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  src={selectedDashboardUrl}
                  title="opensearch"
                  className={`mt-5 h-[550px] w-full border-none transition-opacity duration-200 ${dashboardLoaded ? 'opacity-100' : 'opacity-0'}`}
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  onLoad={() => {
                    try {
                      if (observerRef.current) {
                        observerRef.current.disconnect();
                        observerRef.current = null;
                      }
                      const iframe = iframeRef.current;
                      if (iframe) {
                        const iframeDoc =
                          iframe.contentDocument ||
                          iframe.contentWindow?.document;
                        if (iframeDoc) {
                          removeFullScreenButton(iframeDoc);

                          // Set up a MutationObserver to handle dynamically loaded content
                          const observer = new MutationObserver(() => {
                            removeFullScreenButton(iframeDoc);
                          });

                          observer.observe(iframeDoc.body, {
                            childList: true,
                            subtree: true,
                          });
                          observerRef.current = observer;
                        }
                      }
                    } catch (err) {
                      console.error('Error accessing iframe content:', err);
                    }
                  }}
                />
              </>
            )}
          </div>
        ) : (
          <div
            id="id-dashboard-table-wrapper"
            data-testid="dashboard-table-wrapper"
            className="rounded-tl border border-carbon-borderSubtle bg-white dark:border-gray-700 dark:bg-carbon-darkPanel"
          >
            {/* Table */}
            <Table>
              <TableHeader>
                <TableRow className="border-b border-carbon-borderSubtle bg-carbon-tableHeaderBg/50 dark:border-gray-700 dark:bg-carbon-textPrimary">
                  <TableHead className="text-sm font-semibold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('loop-name')}
                  </TableHead>
                  <TableHead className="text-sm font-semibold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('dashboard-name')}
                  </TableHead>
                  <TableHead className="text-sm font-semibold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('description')}
                  </TableHead>
                  <TableHead className="text-sm font-semibold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('date-created')}
                  </TableHead>
                  <TableHead className="text-sm font-semibold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('status')}
                  </TableHead>
                  <TableHead className="text-sm font-semibold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboards.map((hit) => (
                  <TableRow
                    key={hit._id}
                    className="border-b border-carbon-borderSubtle transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <TableCell className="text-xs text-carbon-textPrimary dark:text-gray-300">
                      {hit._source.dashboard.teamspacename}
                    </TableCell>
                    <TableCell
                      className="cursor-pointer text-sm text-brand-nav hover:underline dark:text-brand-linkDark"
                      onClick={() =>
                        fetchDashboardUrls(
                          hit._source.dashboard.Url,
                          hit._source.dashboard.type,
                          hit._source.dashboard.teamspacename,
                        )
                      }
                    >
                      {hit._source.dashboard.title}
                    </TableCell>
                    <TableCell className="text-xs text-carbon-textPrimary dark:text-gray-300">
                      {hit._source.dashboard.description || '--'}
                    </TableCell>
                    <TableCell className="text-xs text-carbon-textPrimary dark:text-gray-300">
                      {hit._source.dashboard.dateCreated || '--'}
                    </TableCell>
                    <TableCell>
                      <span
                        id="id-dashboard-status-badge"
                        data-testid="dashboard-status-badge"
                        className={cn(
                          'rounded-full px-3 py-1 text-xs text-white',
                          hit._source.dashboard.status === 'Online'
                            ? 'bg-status-online/70'
                            : 'bg-status-offline/70',
                        )}
                      >
                        {hit._source.dashboard.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div
                        id="id-dashboard-actions"
                        data-testid="dashboard-actions"
                        className="flex items-center gap-2"
                      >
                        <button
                          className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchEditableDashboardUrl(
                              hit._source.dashboard.Url + '&show-top-menu=true',
                              hit._source.dashboard.type,
                              hit._source.dashboard.teamspacename,
                            );
                          }}
                          aria-label={t('edit-icon')}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="cursor-not-allowed rounded p-1.5 text-gray-300 dark:text-gray-600"
                          aria-label={t('delete-icon')}
                          disabled
                          title="Delete is not yet available"
                        >
                          <TrashCan size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenSearchDashboardPage;
